import { useMemo, useState } from 'react';
import { IContactForList } from '@nkr/models/index'
import { AgGridReact } from 'ag-grid-react'
import { BodyScrollEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community'
import { GridWrapper } from '../../styles'
import { ActionButtonRenderer } from '../action-button-renderer'
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header'
import Roles from '../roles'

interface IProps {
    data: IContactForList[],
    currentUser?: any,
    fullContactsList?: IContactForList[],
    canHardDelete?: boolean,
    centerId: number,
    leadCoordinatorId: number,
    onGridReady: (e: GridReadyEvent) => void,
    onFirstDataRendered: (e: FirstDataRenderedEvent) => void,
    onMenuSelection: (eventKey: number, contactId: number) => void
}

const CenterGrid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const comparePositions = (x, y) => {
        let xVal = '';
        let yVal = '';

        if (x != undefined && x.length > 0) {
            xVal = x[0].description;
        }

        if (y != undefined && y.length > 0) {
            yVal = y[0].description;
        }

        return xVal.toLowerCase().localeCompare(yVal.toLowerCase());
    };

    const mapPhoneNumbers = (props: ICellRendererParams) => {
        return (
            <table style={{ fontSize: 12, borderCollapse: 'collapse', borderSpacing: 0, lineHeight: '20px', width: 200 }}>
                <tbody>
                    {props.data.phoneNumbers.map((x, index) => {
                        return (
                            <tr key={'ph-' + index}>
                                <td style={{ padding: 0, margin: 0, width: 70, textAlign: 'left' }}>{x.phoneTypeName}:&nbsp;</td>
                                <td style={{ padding: 0, margin: 0, textAlign: 'left' }}>{x.number}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        )
    };

    const columns: ColDef[] = [
        {
            colId: 'rowNumber',
            headerName: 'Name',
            width: 50,
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            colId: 'FirstName',
            headerName: 'First Name',
            field: 'firstName',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) =>
                <>
                    {params.value ? params.value : '--'}
                    {props.leadCoordinatorId == params.data.id ? <a href={`/center/${props.centerId}/profile/edit`}><img src='/images/gold-star-icon.svg' title='Lead Coordinator - click to change' style={{ width: 18, height: 18 }} /></a> : null}
                </>
            ,
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            }
        },
        {
            colId: 'LastName',
            headerName: 'Last Name',
            field: 'lastName',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) =>
                <>
                    {params.value ? params.value : '--'}
                </>
            ,
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            }
        },

        {
            colId: 'Positions',
            headerName: 'Positions',
            field: 'positions',
            sortable: true,
            cellStyle: { lineHeight: '16px' },
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> Position(s) </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => <Roles {...params} />,
            comparator: comparePositions
        },

        {
            colId: 'Email',
            headerName: 'Email',
            field: 'email',
            width: 275,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Email</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) =>
                <>
                    {params.value ? params.value : '--'}
                </>
            ,
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            }
        },

        {
            colId: 'PhoneNumbers',
            headerName: 'PhoneNumbers',
            width: 150,
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Phone Numbers</SortableHeaderComponent>,
            cellRendererFramework: mapPhoneNumbers
        },

        ...(props.canHardDelete ? [{
            colId: 'Status',
            headerName: 'Status',
            width: 100,
            suppressMenu: true,
            suppressMovable: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            cellRendererFramework: (cellProps: ICellRendererParams) =>
                <span style={{ color: cellProps.data.isDeleted ? 'red' : 'green' }}>
                    {cellProps.data.isDeleted ? 'Deleted' : 'Active'}
                </span>
        }] : []),

        {
            colId: 'Actions',
            cellClass: 'action-menu',
            width: 70,
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) =>
                <ActionButtonRenderer
                    {...params}
                    canHardDelete={props.canHardDelete}
                    onMenuToggle={handleMenuToggle}
                    onMenuSelection={props.onMenuSelection}
                    currentUser={props.currentUser} />
        },
    ];

    const columnDefs = useMemo(() => columns, [props.currentUser]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className='ag-theme-bootstrap' style={{ height: '69vh' }}>
            <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={{
                    cellClass: 'center-list-cell',
                    headerClass: 'center-list-header',
                    sortable: true,
                }}
                rowData={rowData}
                getRowHeight={(e: any) => {
                    let data = e.data as IContactForList;
                    const rows = Math.max(data.phoneNumbers.length, data.positions.filter(p => p.id !== -1).length);
                    if (rows <= 1) {
                        return 40;
                    } else {
                        return 25 + (rows * 20);
                    }
                }}
                enableCellTextSelection={true}
                suppressContextMenu={true}
                noRowsOverlayComponentFramework={() => <div>No contacts found...</div>}
                onGridReady={props.onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                onSortChanged={onSortChanged}
                onBodyScroll={onBodyScroll}
            />
            <div style={{ marginTop: 5 }}>{props.data.length} contacts...</div>
        </GridWrapper>
    );
}

export default CenterGrid;